// client/src/hooks/useSocialConfigs.js
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import useAlert from './useAlert'; // Importa useAlert

export const useSocialConfigs = () => {
    const [socialConfigs, setSocialConfigs] = useState([]);
    const [platformsOptions, setPlatformsOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { setAlert } = useAlert(); // Usa o hook de alerta

    // Função para buscar as opções de plataformas do backend
    const fetchPlatformsOptions = useCallback(async () => {
        try {
            const response = await api.get('/admin/institucional/social-configs/options');
            // ✅ CORREÇÃO AQUI: Acessar response.data.platforms, não response.data.data.platforms
            setPlatformsOptions(response.data.data.platforms || []); // Volta para response.data.data.platforms pois seu controller original usava. Se o controller usa `data: { platforms: ... }`, então `response.data.data.platforms` está correto. Se o controller fosse `data: platforms`, então seria `response.data.platforms`. Vamos seguir o que você postou no controller anterior.
        } catch (err) {
            console.error('Erro ao buscar opções de plataformas sociais:', err);
            // setAlert('Erro ao carregar opções de plataformas.', 'error'); // Opcional: alerta para este erro
            // Não interrompe o carregamento principal, apenas loga.
        }
    }, [setAlert]); // Adiciona setAlert como dependência

    const fetchSocialConfigs = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/institucional/social-configs');
            const sortedConfigs = response.data.data.sort((a, b) => a.order - b.order);
            setSocialConfigs(sortedConfigs);
            setError(null); // Limpa erros anteriores
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao buscar configurações sociais';
            setError(errorMessage);
            setAlert(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    const getSocialConfig = useCallback(async (id) => {
        try {
            const response = await api.get(`/admin/institucional/social-configs/${id}`);
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao buscar a configuração social';
            setAlert(errorMessage, 'error');
            throw new Error(errorMessage);
        }
    }, [setAlert]);

    const createSocialConfig = useCallback(async (configData) => {
        try {
            const response = await api.post('/admin/institucional/social-configs', configData);
            setSocialConfigs(prev => [...prev, response.data.data].sort((a, b) => a.order - b.order));
            setAlert('Rede social criada com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao criar configuração social';
            setAlert(errorMessage, 'error');
            throw new Error(errorMessage);
        }
    }, [setAlert]);

    const updateSocialConfig = useCallback(async (id, configData) => {
        try {
            const response = await api.put(`/admin/institucional/social-configs/${id}`, configData);
            setSocialConfigs(prev => prev.map(config =>
                config._id === id ? response.data.data : config
            ).sort((a, b) => a.order - b.order));
            setAlert('Rede social atualizada com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar configuração social';
            setAlert(errorMessage, 'error');
            throw new Error(errorMessage);
        }
    }, [setAlert]);

    const deleteSocialConfig = useCallback(async (id) => {
        try {
            await api.delete(`/admin/institucional/social-configs/${id}`);
            setSocialConfigs(prev => prev.filter(config => config._id !== id));
            setAlert('Rede social excluída com sucesso!', 'success');
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao deletar configuração social';
            setAlert(errorMessage, 'error');
            throw new Error(errorMessage);
        }
    }, [setAlert]);

    const updateSocialConfigsOrder = useCallback(async (orderedConfigs) => {
        try {
            const configsToUpdate = orderedConfigs.map((config, index) => ({ _id: config._id, order: index }));
            const response = await api.put('/admin/institucional/social-configs/update-order', {
                socialConfigs: configsToUpdate
            });
            // O backend deve retornar a lista reordenada ou um sucesso.
            // Para garantir que o estado local está alinhado, atualizamos com o que foi enviado/confirmado.
            setSocialConfigs(orderedConfigs); 
            setAlert('Ordem das redes sociais atualizada!', 'success');
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar ordem das redes sociais';
            setAlert(errorMessage, 'error');
            throw new Error(errorMessage);
        }
    }, [setAlert]);

    useEffect(() => {
        fetchSocialConfigs();
        fetchPlatformsOptions(); // Busca as opções de plataformas ao montar
    }, [fetchSocialConfigs, fetchPlatformsOptions]);

    return {
        socialConfigs,
        setSocialConfigs, // ✅ Exporta setSocialConfigs para permitir updates diretos (Drag&Drop)
        platformsOptions,
        loading,
        error,
        getSocialConfig,
        createSocialConfig,
        updateSocialConfig,
        deleteSocialConfig,
        updateSocialConfigsOrder,
        refetch: fetchSocialConfigs // Para recarregar todos os dados
    };
};